/**
 * Spray source of a competition — turns the spray half of the daily climb
 * logs into the climbs `computeChallengeStandings` scores (a spray challenge),
 * and prices a problem for a spray competition.
 *
 * A challenge with `source: "spray"` is the same passive board as a route
 * challenge, over the gym's Spraywall problems. Only BENCHMARK-eligible
 * problems score (anybody can create and grade a problem; a benchmark is set
 * by a moderator on a public problem, so a self-made "8a" cannot buy the
 * lead), and a problem is worth the points of the gym difficulty level its
 * Font grade falls in. Eligibility and grade are read at compute time — the
 * problem's current state, like the benchmark level.
 *
 * Spec: docs/spec/02d-shared-spraywall.md ("Spray challenge source
 * (sprayChallenge.js)") and docs/spec/09-challenge.md ("Spray source").
 */
import { sprayDayLogEntries } from "./sprayDayLog.js";
import { isBenchmarkEligible, problemGrade } from "./sprayLevel.js";
import { gradeBands, difficultyLevelOfGrade } from "./gradeBands.js";

const isMap = (v) => Object.prototype.toString.call(v) === "[object Object]";

/**
 * The source of a challenge document. The security rules do not check the
 * field, so anything but the exact string "spray" reads as "routes" — an old
 * document without the field included.
 */
export function challengeSource(comp) {
  return isMap(comp) && comp.source === "spray" ? "spray" : "routes";
}

/**
 * The source of any competition: a challenge keeps it on the document
 * (`source`), a standard competition in `settings.source`. Anything but the
 * exact string "spray" reads as "routes".
 */
export function competitionSource(comp) {
  if (!isMap(comp)) return "routes";
  if (comp.type === "challenge") return challengeSource(comp);
  return isMap(comp.settings) && comp.settings.source === "spray" ? "spray" : "routes";
}

/**
 * A spray problem's competition points: those of the difficulty level its
 * grade falls in, 0 when none. No benchmark check — the picker lists only
 * benchmark problems, and a point fixed at save must not move if the mark is
 * taken back later (the competition's list counts, decision S13).
 */
export function sprayProblemCompPoints(problem, difficultyConfig) {
  const level = difficultyLevelOfGrade(difficultyConfig, problemGrade(problem));
  const n = Number(level && level.points);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * True when the gym's difficulty config has at least one level with a Font
 * `gradeRange`. Without one every spray climb would score 0 (the built-in
 * default ladder has none), so the spray source is not offered.
 */
export function hasSprayGradeBands(difficultyConfig) {
  return gradeBands(difficultyConfig).some((b) => !b.fallback);
}

/**
 * The scorer climbs of a spray challenge, one per accepted log entry (not
 * deduped — `computeChallengeStandings` keeps one per climber and problem).
 * @param {Array} dayLogs - `{ userId, date, spray }` daily climb-log documents of the window
 * @param {object} problemsById - `{ [problemId]: problem }` (the benchmark-marked ones are enough)
 * @param {object|null} difficultyConfig - the gym's resolved boulder difficulty config
 * @returns {Array<{ userId, problemId, points, flash, kind, at, date, routeDiscipline }>}
 */
export function sprayChallengeClimbs(dayLogs, problemsById, difficultyConfig) {
  if (!Array.isArray(dayLogs) || !isMap(problemsById)) return [];
  const out = [];
  // Points are per problem; one resolution per problem, however many climbers sent it.
  const pointsCache = new Map();
  const pointsOf = (problemId) => {
    if (pointsCache.has(problemId)) return pointsCache.get(problemId);
    const problem = Object.prototype.hasOwnProperty.call(problemsById, problemId) ? problemsById[problemId] : null;
    let points = 0;
    if (isBenchmarkEligible(problem)) {
      const level = difficultyLevelOfGrade(difficultyConfig, problemGrade(problem));
      const n = Number(level && level.points);
      points = Number.isFinite(n) && n > 0 ? n : 0;
    }
    pointsCache.set(problemId, points);
    return points;
  };
  for (const log of dayLogs) {
    if (!isMap(log) || typeof log.userId !== "string" || !log.userId) continue;
    const spray = isMap(log.spray) ? log.spray : null;
    if (!spray) continue;
    for (const e of sprayDayLogEntries([log])) {
      if (e.kind !== "send" && e.kind !== "repeat") continue;
      // A spray competition top (written by the close sync) is not challenge climbing.
      if (spray[e.entryId] && spray[e.entryId].compId) continue;
      const points = pointsOf(e.problemId);
      if (points <= 0) continue;
      out.push({
        userId: log.userId,
        problemId: e.problemId,
        points,
        flash: e.kind === "send" && e.flash,
        kind: e.kind,
        at: e.at,
        date: e.date,
        routeDiscipline: "boulder",
      });
    }
  }
  return out;
}
